import React from 'react';
import { X, Shield, Eye, Clock, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import incognitoModeImg from '@/assets/incognito_mode.png';
import Icon from '@mdi/react';
import { mdiIncognitoCircle } from '@mdi/js';

interface IncognitoModeModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode?: boolean;
}

export const IncognitoModeModal: React.FC<IncognitoModeModalProps> = ({
  isOpen,
  onClose,
  isDarkMode = false
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-md mx-4 rounded-2xl shadow-2xl border-2 ${
        isDarkMode 
          ? 'bg-[#1a1a1a] border-[#2e2e2e]' 
          : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:py-4 py-2">
          <div className="flex items-center gap-3">
            <h2 className={`text-xl font-semibold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Incognito Mode
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className={`h-8 w-8 rounded-full ${
              isDarkMode 
                ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="sm:px-6 px-3 sm:pb-6 pb-4">
          {/* Incognito Image */}
          <div className="flex justify-center mb-6">
            <div className={`relative p-4 rounded-2xl bg-transparent`}>
              <img 
                src={incognitoModeImg} 
                alt="Incognito Mode" 
                className="h-[150px] w-[150px] object-contain"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-4 mb-6">
            <p className={`text-center text-sm leading-relaxed ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              You're now in <span className="font-semibold text-blue-500">Incognito Mode</span>. 
              Your conversations are private and temporary. 
            </p>
            <ul className={`list-disc list-inside text-xs sm:text-sm ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              <li>Chats won't appear in your history</li>
              <li>Messages are cleared on refresh or close</li>
              <li>Nothing is stored in your account or device</li>
            </ul>
          </div>

          {/* Action Button */}
          <Button
            onClick={onClose}
            className={`w-full h-11 rounded-full font-medium transition-all duration-200 ${
              isDarkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            Continue
          </Button>

          {/* Footer Note */}
          <p className={`hidden text-center text-xs mt-4 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`}>
            
          </p>
        </div>
      </div>
    </div>
  );
};
